import { useContext, useEffect, useState, useCallback, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  Box,
  Portal,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import SearchBar from 'components/searchBar';
import { NotificationBell } from 'components/notifications/NotificationBell';

import { SidebarResponsive } from 'layouts/admin/sidebar/Sidebar';

import { AuthContext } from 'contexts/AuthContext';

import { apiSearch, apiSignOut } from 'api';

import type { APIPaginatedResponse, APISearchData } from 'types/api.types';

const getSearchResult = (result: APISearchData) => {
  // Build title with policy number and customer name
  const customerName = result.customer_name || 'N/A';

  let title = result.policy_nr
    ? `Policy #${result.policy_nr}`
    : result.uuid.substring(0, 8) + '...';
  title += ` • Customer: ${customerName}`;

  // Build footnote with all available information
  const footnoteDetails = [];

  const createdDate = result.created_at
    ? new Date(result.created_at).toLocaleDateString()
    : 'N/A';
  const broker = result.broker || 'N/A';

  footnoteDetails.push(`Created ${createdDate}`);
  footnoteDetails.push(`Broker: ${broker}`);

  const output = {
    id: result.uuid,
    title: title,
    footnote: footnoteDetails.join(' • '),
  };

  return output;
};

const TopNavigationTools = ({ theme, setTheme }: { [x: string]: any }) => {
  const { user, authDispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );
  const avatarBg = useColorModeValue('gray.700', 'gray.600');

  const handleLogOut = () => {
    // Fire and forget - don't wait for response
    apiSignOut().catch(() => {});

    queryClient.clear();
    authDispatch({ type: 'remove_user' });
    navigate('/');
  };

  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback((value: string) => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim()) {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const response = await apiSearch(value);
          const data = response as APIPaginatedResponse<APISearchData>;

          const formattedResults = data.results.map((result) =>
            getSearchResult(result),
          );

          setSearchResults(formattedResults);
        } catch (error) {
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 500);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleResultClick = (result: any) => {
    navigate(`/admin/policies/overview/${result.id}`);
  };

  return (
    <Flex
      w={{ sm: '100%', md: 'auto' }}
      alignItems="center"
      flexDirection="row"
      bg={menuBg}
      flexWrap={'unset'}
      p="10px"
      borderRadius="999px"
      boxShadow={shadow}
      className="pink-neon-glow-subtle"
    >
      <Box me="10px">
        <SearchBar
          onChange={(e) => handleSearch(e.target.value)}
          results={searchResults}
          isLoading={isSearching}
          onResultClick={handleResultClick}
        />
      </Box>

      <SidebarResponsive />

      <Box me="10px">
        <NotificationBell />
      </Box>

      <Box>
        <Menu>
          <MenuButton p="0px">
            <Avatar
              _hover={{ cursor: 'pointer' }}
              src={user.organization.logo}
              size="sm"
              w="40px"
              h="40px"
              bg={avatarBg}
            />
          </MenuButton>
          <Portal>
            <MenuList
              boxShadow={shadow}
              p="0px"
              mt="10px"
              borderRadius="20px"
              bg={menuBg}
              border="none"
              zIndex={10000}
            >
              <Flex w="100%" mb="0px">
                <Text
                  ps="20px"
                  pt="16px"
                  pb="10px"
                  w="100%"
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  fontSize="sm"
                  fontWeight="700"
                  color={textColor}
                >
                  👋&nbsp; Hey, {user.first_name}
                </Text>
              </Flex>
              <Flex flexDirection="column" p="10px">
                <MenuItem
                  _hover={{ bg: 'none' }}
                  _focus={{ bg: 'none' }}
                  color="red.400"
                  borderRadius="8px"
                  px="14px"
                  onClick={handleLogOut}
                >
                  <Text fontSize="sm">Log out</Text>
                </MenuItem>
              </Flex>
            </MenuList>
          </Portal>
        </Menu>
      </Box>
    </Flex>
  );
};

export default TopNavigationTools;
